import { FunctionComponent, useState } from 'react'
import { useMutation } from '@apollo/client'
import { RELEASE_SCHEDULED_OCCASIONAL_SLOT } from '@/modules/API/slot/occasionalSlotHandler'
import { useTranslation } from 'react-i18next'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import DatePicker from '@/components/_custom/Picker/datePicker/datePicker'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Daycare, DaycareType } from '@/types/Daycare'
import styles from './releaseScheduledOccasionalSlot.module.scss'
import { Section } from '@/types/Section'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface ReleaseScheduledOccasionalSlotProps {
    open: boolean
    handleClose: () => void
    daycare: Daycare
    refetchData: () => void
}

const ReleaseScheduledOccasionalSlot: FunctionComponent<ReleaseScheduledOccasionalSlotProps> = ({
    open,
    handleClose,
    daycare,
    refetchData
}: ReleaseScheduledOccasionalSlotProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [untilDate, setUntilDate] = useState<string>('')

    const [selectedSections, setSelectedSections] = useState<string[]>(
        daycare.sections ? daycare.sections.map((section: Section) => section.id.toString()) : []
    )

    const sectionsOptions: SelectOptionsDeprecated = [
        {
            options:
                daycare.sections?.map(
                    (section: Section): SelectOptionDeprecated => ({
                        value: section.id.toString(),
                        name: section.name.valueOf()
                    })
                ) || []
        }
    ]

    const [releaseScheduledOccasionalSlots, { loading: loadingRelease }] = useMutation(
        RELEASE_SCHEDULED_OCCASIONAL_SLOT,
        {
            variables: {
                daycare_id: daycare.id,
                sectionIds: selectedSections.map((section: string) => parseInt(section)),
                until: untilDate === '' ? undefined : untilDate
            },
            onCompleted: () => {
                refetchData()
                handleClose()

                showNotificationSuccess(t('slotsReleased'))
            },
            onError: () => {
                showNotificationError(t('errorReleasedSlots'))
            }
        }
    )

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('liberatedSlots')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.Input}>
                    <DatePicker
                        value={untilDate}
                        setValue={setUntilDate}
                        disablePast
                        label={t('releaseSlotUntil')}
                        className={styles.UserInput}
                    />
                </div>
                {daycare.sections && daycare.sections.length > 1 && (
                    <div className={styles.Input}>
                        <SelectMultiple
                            selectOptions={sectionsOptions}
                            selectValues={selectedSections}
                            label={isRpeOrLaep ? t('mySectors') : t('mySections')}
                            setSelectValues={setSelectedSections}
                            className={styles.UserInput}
                        />
                    </div>
                )}
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'confirm'}
                        onClick={releaseScheduledOccasionalSlots}
                        className={styles.Button}
                        disabled={loadingRelease || selectedSections.length === 0}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default ReleaseScheduledOccasionalSlot
